import { useState, useCallback, ChangeEvent, useRef, useEffect } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Input, message, Tooltip, Typography, Switch } from 'antd';
import * as Icons from '@ant-design/icons';

import { useService } from '@core/inversify-react';

import { HeaderButton } from '@modules/layout';

import { GetDeclarationByTrackCodeRepoType, GetPartnerDeclarationByTrackCodeRepoType, IGetDeclarationByTrackCodeRepo, IGetPartnerDeclarationByTrackCodeRepo } from '@modules/declarations';
import { useAuth } from '@modules/auth';

export const Form = styled.form`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 10px;
`;

export const QuickSearch = () => {
  const [isInputShown, setIsInputShown] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [isTrendyol, setIsTrendyol] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const enterPressedRef = useRef(false);
  const inputRef = useRef<Input | null>(null);
  const history = useHistory();
  const { canDisplay } = useAuth();

  const getDeclarationByTrackCodeRepo = useService<IGetDeclarationByTrackCodeRepo>(GetDeclarationByTrackCodeRepoType);
  const getPartnerDeclarationByTrackCodeRepo = useService<IGetPartnerDeclarationByTrackCodeRepo>(GetPartnerDeclarationByTrackCodeRepoType);

  const checkDeclaration = useCallback(
    async (trackCode: string) => {
      const repository = canDisplay('partner') ? getPartnerDeclarationByTrackCodeRepo : getDeclarationByTrackCodeRepo;

      setIsLoading(true);
      const result = await repository.execute({ trackCode, trendyol: isTrendyol ? '1' : undefined });
      setIsLoading(false);

      if (result.status === 200) {
        if (result.response.id) {
          const declarationId = result.response.id;
          history.replace(generatePath('/@next/declarations/:declarationId', { declarationId }), declarationId);
        }
      } else {
        message.error(result.response);
      }
    },
    [getDeclarationByTrackCodeRepo, history, getPartnerDeclarationByTrackCodeRepo, canDisplay, isTrendyol],
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      enterPressedRef.current = true;
      checkDeclaration(searchInput);
    },
    [checkDeclaration, searchInput],
  );

  const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  }, []);

  // Klaviatura qısa yolları: Ctrl/Cmd+K fokus; ESC təmizlə
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isCmdK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (isCmdK) {
        e.preventDefault();
        setIsInputShown(true);
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      if (isInputShown && e.key === 'Escape') {
        setSearchInput('');
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isInputShown]);

  const handleBlur = useCallback(() => {
    if (enterPressedRef.current) {
      enterPressedRef.current = false;
      return;
    }
    setIsInputShown(false);
  }, []);

  const onButtonClick = useCallback(() => {
    setIsInputShown(true);
  }, []);

  const inputPrefix = isLoading ? <Icons.LoadingOutlined /> : <Icons.SearchOutlined />;
  const inputSuffix = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {/* Clear icon - prevent blur */}
      {searchInput?.length > 0 && (
        <Icons.CloseCircleFilled
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            setSearchInput('');
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          style={{ color: '#bfbfbf', fontSize: 14, cursor: 'pointer' }}
        />
      )}
      {/* Trendyol toggle with tooltip */}
      <span
        onMouseDown={(e) => {
          // Prevent input from losing focus when clicking the toggle
          e.preventDefault();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip
          mouseEnterDelay={0.2}
          placement='bottomRight'
          overlayInnerStyle={{ background: '#ffffff', color: '#262626', boxShadow: '0 6px 16px rgba(0,0,0,0.2)', padding: 10, borderRadius: 6 }}
          title={
            <div style={{ maxWidth: 280 }}>
              <Typography.Text strong style={{ color: '#262626' }}>
                Trendyol axtarışı
              </Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>Yanılı olduqda yalnız Trendyol izləmə kodu (FDX000000001) ilə axtarılır.</Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>Sönülü olduqda əvvəlcə bağlamanın izləmə kodu, tapılmazsa qlobal izləmə kodu ilə axtarılır.</Typography.Text>
            </div>
          }
        >
          <Switch size='small' checked={isTrendyol} onChange={(checked) => setIsTrendyol(checked)} style={{ marginRight: 0 }} />
        </Tooltip>
      </span>
    </span>
  );

  return (
    <Form onSubmit={onSubmit}>
      {isInputShown && (
        <Input
          ref={inputRef}
          disabled={isLoading}
          prefix={inputPrefix}
          suffix={inputSuffix}
          autoFocus={true}
          placeholder='İzləmə kodu'
          value={searchInput}
          onChange={onInputChange}
          onBlur={handleBlur}
          onPressEnter={() => checkDeclaration(searchInput)}
          style={{ borderRadius: 9999, height: 36, paddingLeft: 12, paddingRight: 12 }}
        />
      )}
      <HeaderButton onClick={onButtonClick} icon={<Icons.SearchOutlined />}></HeaderButton>
    </Form>
  );
};
